import { MessageBar, MessageBarType } from "office-ui-fabric-react";
import React from "react";
import { Field, getOptions } from "../../entities/Metadata";
import { IProcessStage, ApprovalType } from "../../entities/Subentities";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { IInputProps } from "../common/interfaces/IInputProps";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import { PPMFeatures, Subscription } from "../../store/Tenant";
import DropdownInput, { ColorItemOption } from "../common/inputs/DropdownInput";

type OwnProps = {
    inputProps: IInputProps;
    state: IProcessStage;
    field: Field;
};

type StateProps = {
    subscription: Subscription;
};

type Props = OwnProps & StateProps;

const approvalTypeOrder = [ApprovalType.None, ApprovalType.InApp, ApprovalType.GateDecision, ApprovalType.APIBased];

const ApprovalForTransitions = ({ inputProps, state, field, subscription }: Props) => {
    const [showApiApprovalConfirmation, setShowApiApprovalConfirmation] = React.useState<boolean>(false);
    const [fieldValue, setFieldValue] = React.useState<ApprovalType | undefined>();

    const options = getOptionsWithPlanGating(field, subscription);

    return (
        <>
            {showApiApprovalConfirmation && (
                <ConfirmationDialog
                    onDismiss={() => setShowApiApprovalConfirmation(false)}
                    onNo={() => {}}
                    onYes={() => inputProps.onChanged?.(fieldValue)}
                    yesButtonProps={{ text: "Confirm" }}
                    noButtonProps={{ text: "Cancel" }}
                    dialogContentProps={{
                        title: "Confirmation",
                        subText: `Are you sure you want to turn API-based Approval on for transitioning from this stage to the next stage?`
                    }}>
                    <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                        An external approval process will need to be created, this process will be triggered via API event
                        when user tries to go to the next stage. Transition will only happen once external approval process
                        is completed, and an API event is sent back to PPM Express confirming transition.
                    </MessageBar>
                </ConfirmationDialog>
            )}

            <DropdownInput
                {...inputProps}
                inputProps={{ options, readOnly: field.isReadonly }}
                value={state.attributes.ApprovalForTransition ?? field.defaultValue}
                onChanged={(value: any) => {
                    if (value === ApprovalType.APIBased) {
                        setShowApiApprovalConfirmation(true);
                        setFieldValue(value);
                    } else {
                        inputProps.onChanged?.(value);
                    }
                }}
            />
        </>
    );
};

const upgradeMessage = "Upgrade your subscription to use this option.";

function getOptionsWithPlanGating(approvalField: Field, subscription: Subscription): ColorItemOption[] {
    const options: ColorItemOption[] = getOptions(approvalField);
    const apiOptionState = getApiBasedOptionState(subscription);
    const inAppOptionState = getInAppOptionState(subscription);
    const gateDecisionOptionState = getGateDecisionOptionState(subscription);

    return approvalTypeOrder
        .map(type => options.find(_ => _.key === type))
        .filter((opt): opt is ColorItemOption => !!opt)
        .map(opt => {
            if (opt.key === ApprovalType.APIBased) {
                return { ...opt, ...apiOptionState };
            }
            if (opt.key === ApprovalType.InApp) {
                return { ...opt, ...inAppOptionState };
            }
            if (opt.key === ApprovalType.GateDecision) {
                return { ...opt, ...gateDecisionOptionState };
            }
            return opt;
        });
}

export function getApiBasedOptionState(subscription: Subscription): { disabled: boolean; title?: string } {
    const hasApiFeature = Subscription.contains(subscription, PPMFeatures.API);
    return {
        disabled: !hasApiFeature,
        title: hasApiFeature ? undefined : upgradeMessage
    };
}

function getInAppOptionState(subscription: Subscription): { disabled: boolean; title?: string } {
    const hasFeature = Subscription.contains(subscription, PPMFeatures.StageStandardApprovals);
    return {
        disabled: !hasFeature,
        title: hasFeature ? undefined : upgradeMessage
    };
}

function getGateDecisionOptionState(subscription: Subscription): { disabled: boolean; title?: string } {
    const hasFeature = Subscription.contains(subscription, PPMFeatures.StageGates);
    return {
        disabled: !hasFeature,
        title: hasFeature ? undefined : upgradeMessage
    };
}

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        subscription: state.tenant.subscription
    };
}

export default connect(mapStateToProps)(ApprovalForTransitions);
